import React, { useState, useMemo, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Plus, X, Package, ChevronRight, Trash2, AlertTriangle } from "lucide-react";

const STATO_META = {
  inviata:         { label: "📮 Inviata",         className: "bg-blue-100 text-blue-800 border-blue-200" },
  in_preparazione: { label: "🔧 In preparazione", className: "bg-amber-100 text-amber-800 border-amber-200" },
  pronta:          { label: "✅ Pronta",          className: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  consegnata:      { label: "📦 Consegnata",      className: "bg-slate-200 text-slate-800 border-slate-300" },
  annullata:       { label: "❌ Annullata",       className: "bg-rose-100 text-rose-800 border-rose-200" },
};
const NEXT_STATO = {
  inviata: ["in_preparazione", "annullata"],
  in_preparazione: ["pronta", "annullata"],
  pronta: ["consegnata", "annullata"],
};

export default function RichiesteMagazzino() {
  const { user } = useAuth();
  const isOps = user && ["amministratore", "impiegato_amministrativo", "responsabile_cantiere"].includes(user.role);
  const isAdmin = user && user.role === "amministratore";
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [filterStato, setFilterStato] = useState("all");
  const [confermaDel, setConfermaDel] = useState(null); // richiesta da eliminare
  const [scannerFor, setScannerFor] = useState(null);

  const { data: items = [] } = useQuery({
    queryKey: ["richieste-magazzino"],
    queryFn: () => api.get("/richieste-magazzino").then((r) => r.data),
  });
  const { data: cantieri = [] } = useQuery({
    queryKey: ["cantieri"],
    queryFn: () => api.get("/cantieri").then((r) => r.data),
  });
  const { data: materiali = [] } = useQuery({
    queryKey: ["magazzino-materiali"],
    queryFn: () =>
      api
        .get("/magazzino/materiali")
        .then((r) => r.data)
        .catch(() => []),
  });

  const filtered = useMemo(
    () => (filterStato === "all" ? items : items.filter((r) => r.stato === filterStato)),
    [items, filterStato]
  );

  const [form, setForm] = useState({
    cantiere_id: "",
    materiale_id: "",
    materiale_nome: "",
    quantita: 1,
    unita_misura: "pz",
    note: "",
    fromMagazzino: true,
  });

  const create = useMutation({
    mutationFn: (p) => api.post("/richieste-magazzino", p).then((r) => r.data),
    onSuccess: () => {
      toast.success("Richiesta inviata all'ufficio magazzino");
      qc.invalidateQueries({ queryKey: ["richieste-magazzino"] });
      setOpen(false);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const changeStato = useMutation({
    mutationFn: ({ id, stato, note }) =>
      api.patch(`/richieste-magazzino/${id}/stato`, { stato, note: note || "" }).then((r) => r.data),
    onSuccess: (data, variables) => {
      qc.invalidateQueries({ queryKey: ["richieste-magazzino"] });

      if (variables?.stato === "in_preparazione" && data?.materiale_id) {
        setScannerFor(data);
      }
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  // Riutilizza l'endpoint DELETE esistente (soft-delete via `deleted_at`).
  // La lista viene ricaricata e i chip contatori si aggiornano automaticamente
  // perché derivano da `items`.
  const eliminaRichiesta = useMutation({
    mutationFn: (id) => api.delete(`/richieste-magazzino/${id}`).then((r) => r.data),
    onSuccess: () => {
      toast.success("Richiesta eliminata");
      qc.invalidateQueries({ queryKey: ["richieste-magazzino"] });
      setConfermaDel(null);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Eliminazione fallita"),
  });

  const submit = () => {
    if (!form.cantiere_id) return;
    const payload = {
      cantiere_id: form.cantiere_id,
      materiale_id: form.fromMagazzino ? form.materiale_id : null,
      materiale_nome: form.materiale_nome,
      quantita: parseFloat(form.quantita),
      unita_misura: form.unita_misura,
      note: form.note,
    };
    create.mutate(payload);
  };

  return (
    <div className="space-y-6" data-testid="page-richieste-magazzino">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="overline">Magazzino</div>
          <h1 className="font-display text-3xl xl:text-4xl font-black">Richieste materiali</h1>
          <p className="text-slate-500 text-sm mt-1">
            {isOps
              ? "Gestisci le richieste dei cantieri: prepara, prepara e consegna."
              : "Richiedi materiale per il tuo cantiere. Riceverai una notifica ad ogni cambio di stato."}
          </p>
        </div>
        <button
          onClick={() => {
            setForm({
              cantiere_id: "",
              materiale_id: "",
              materiale_nome: "",
              quantita: 1,
              unita_misura: "pz",
              note: "",
              fromMagazzino: materiali.length > 0,
            });
            setOpen(true);
          }}
          data-testid="btn-new-richiesta-magazzino"
          className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-sm shadow-brutal"
        >
          <Plus className="w-4 h-4" /> Nuova richiesta
        </button>
      </div>

      {/* Filtro stati */}
      <div className="flex flex-wrap gap-2">
        <FilterChip active={filterStato === "all"} onClick={() => setFilterStato("all")} label={`Tutte (${items.length})`} />
        {Object.keys(STATO_META).map((s) => (
          <FilterChip
            key={s}
            active={filterStato === s}
            onClick={() => setFilterStato(s)}
            label={`${STATO_META[s].label} (${items.filter((i) => i.stato === s).length})`}
          />
        ))}
      </div>

      <div className="bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left p-3">Data</th>
              <th className="text-left p-3">Lavoratore</th>
              <th className="text-left p-3">Cantiere</th>
              <th className="text-left p-3">Materiale</th>
              <th className="text-right p-3">Q.tà</th>
              <th className="text-left p-3">Stato</th>
              <th className="text-left p-3">Note</th>
              <th className="text-right p-3">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.richiesta_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`row-rmag-${r.richiesta_id}`}>
                <td className="p-3 font-mono text-xs">{(r.created_at || "").slice(0, 10)}</td>
                <td className="p-3">{r.lavoratore_nome}</td>
                <td className="p-3">{r.cantiere_nome}</td>
                <td className="p-3 font-medium">{r.materiale_nome}</td>
                <td className="p-3 text-right font-mono">{r.quantita} {r.unita_misura}</td>
                <td className="p-3">
                  <span className={`inline-flex items-center px-2 py-1 rounded-sm text-[11px] font-semibold border ${STATO_META[r.stato]?.className || ""}`}>
                    {STATO_META[r.stato]?.label || r.stato}
                  </span>
                </td>
                <td className="p-3 text-xs text-slate-600 max-w-xs truncate" title={r.note}>{r.note || "—"}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  <div className="flex justify-end items-center gap-1">
                    {isOps && NEXT_STATO[r.stato]?.length > 0 && (
                      <>
                        {NEXT_STATO[r.stato].map((next) => (
                          <button
                            key={next}
                            onClick={() => changeStato.mutate({ id: r.richiesta_id, stato: next })}
                            data-testid={`btn-stato-${r.richiesta_id}-${next}`}
                            className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded-sm border ${
                              next === "annullata"
                                ? "border-rose-200 text-rose-700 hover:bg-rose-50"
                                : "border-blue-200 text-blue-700 hover:bg-blue-50"
                            }`}
                          >
                            {STATO_META[next]?.label} <ChevronRight className="w-3 h-3" />
                          </button>
                        ))}
                      </>
                    )}
                    {isOps &&
                      r.materiale_id &&
                      !["consegnata", "annullata"].includes(r.stato) && (
                        <button
                          type="button"
                          onClick={() => setScannerFor(r)}
                          data-testid={`btn-preleva-rmag-${r.richiesta_id}`}
                          title={`Apri richiesta e preleva ${r.materiale_nome}`}
                          className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded-sm border border-amber-300 text-amber-800 hover:bg-amber-50"
                        >
                          <Package className="w-3.5 h-3.5" />
                          {r.stato === "in_preparazione"
                            ? "Riapri preparazione"
                            : "Apri / Preleva"}
                        </button>
                      )}

                    {!isOps && r.stato === "inviata" && r.lavoratore_email === user?.email && (
                      <button
                        onClick={() => changeStato.mutate({ id: r.richiesta_id, stato: "annullata" })}
                        className="text-[11px] font-semibold text-rose-700 hover:text-rose-900"
                      >
                        Annulla
                      </button>
                    )}
                    {isAdmin && (
                      <button
                        onClick={() => setConfermaDel(r)}
                        data-testid={`btn-elimina-rmag-${r.richiesta_id}`}
                        title="Elimina richiesta"
                        className="p-1.5 text-rose-700 hover:bg-rose-100 rounded-sm ml-1"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan="8" className="p-8 text-center text-slate-400">
                  <Package className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  Nessuna richiesta {filterStato !== "all" ? `in stato "${filterStato}"` : ""}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {scannerFor && (
        <PrelievoScannerModal
          richiesta={scannerFor}
          onClose={() => {
            setScannerFor(null);
            qc.invalidateQueries({ queryKey: ["richieste-magazzino"] });
            qc.invalidateQueries({ queryKey: ["magazzino-materiali"] });
            qc.invalidateQueries({ queryKey: ["magazzino"] });
            qc.invalidateQueries({ queryKey: ["magazzino-movimenti"] });
          }}
          onUpdated={(richiestaAggiornata) => {
            setScannerFor(richiestaAggiornata);
            qc.invalidateQueries({ queryKey: ["richieste-magazzino"] });
            qc.invalidateQueries({ queryKey: ["magazzino-materiali"] });
            qc.invalidateQueries({ queryKey: ["magazzino"] });
            qc.invalidateQueries({ queryKey: ["magazzino-movimenti"] });
          }}
        />
      )}

      {confermaDel && (
        <ConfermaEliminaModal
          richiesta={confermaDel}
          onCancel={() => setConfermaDel(null)}
          onConfirm={() => eliminaRichiesta.mutate(confermaDel.richiesta_id)}
          pending={eliminaRichiesta.isPending}
        />
      )}

      {open && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white w-full max-w-xl border border-slate-200 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="richiesta-magazzino-modal">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <h2 className="font-display font-bold text-xl">Nuova richiesta materiale</h2>
              <button onClick={() => setOpen(false)}><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Cantiere *</label>
                <select value={form.cantiere_id} onChange={(e) => setForm({ ...form, cantiere_id: e.target.value })} data-testid="input-cantiere" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm">
                  <option value="">— seleziona —</option>
                  {cantieri.map((c) => (
                    <option key={c.cantiere_id} value={c.cantiere_id}>{c.nome}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Materiale</label>
                <div className="flex items-center gap-2 mb-2 text-xs">
                  <label className="inline-flex items-center gap-1">
                    <input type="radio" checked={form.fromMagazzino} onChange={() => setForm({ ...form, fromMagazzino: true, materiale_nome: "" })} />
                    <span>Dal magazzino ({materiali.length})</span>
                  </label>
                  <label className="inline-flex items-center gap-1">
                    <input type="radio" checked={!form.fromMagazzino} onChange={() => setForm({ ...form, fromMagazzino: false, materiale_id: "" })} />
                    <span>Altro (testo libero)</span>
                  </label>
                </div>
                {form.fromMagazzino ? (
                  <select
                    value={form.materiale_id}
                    onChange={(e) => {
                      const m = materiali.find((x) => x.materiale_id === e.target.value);
                      setForm({
                        ...form,
                        materiale_id: e.target.value,
                        materiale_nome: m ? m.nome : "",
                        unita_misura: m?.unita_misura || form.unita_misura,
                      });
                    }}
                    data-testid="input-materiale-select"
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                  >
                    <option value="">— seleziona —</option>
                    {materiali.map((m) => (
                      <option key={m.materiale_id} value={m.materiale_id}>
                        {m.nome} · {m.categoria || "—"}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    value={form.materiale_nome}
                    onChange={(e) => setForm({ ...form, materiale_nome: e.target.value })}
                    placeholder="Es. Cemento CEM II 42.5, sacchi da 25 kg"
                    data-testid="input-materiale-free"
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                  />
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-600 block mb-1">Quantità *</label>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    value={form.quantita}
                    onChange={(e) => setForm({ ...form, quantita: parseFloat(e.target.value || 0) })}
                    data-testid="input-quantita"
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm font-mono"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 block mb-1">Unità di misura</label>
                  <input
                    value={form.unita_misura}
                    onChange={(e) => setForm({ ...form, unita_misura: e.target.value })}
                    data-testid="input-unita"
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Note</label>
                <textarea
                  rows={3}
                  value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                  placeholder="Urgenza, dettagli, riferimenti…"
                  data-testid="input-note"
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                />
              </div>
            </div>
            <div className="p-5 border-t border-slate-200 flex justify-end gap-2">
              <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm border border-slate-300 rounded-sm hover:bg-slate-50">
                Annulla
              </button>
              <button
                onClick={submit}
                disabled={create.isPending}
                data-testid="btn-submit-richiesta"
                className="px-4 py-2 text-sm bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white font-semibold rounded-sm"
              >
                {create.isPending ? "Invio…" : "Invia richiesta"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FilterChip({ active, onClick, label }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${
        active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"
      }`}
    >
      {label}
    </button>
  );
}

/**
 * Modal di conferma eliminazione richiesta materiali.
 * Avvisa se la richiesta è già in preparazione/pronta/consegnata e riporta
 * che eventuali movimenti magazzino già registrati NON vengono toccati
 * (la richiesta viene solo soft-deleted / spostata nel cestino).
 */
function ConfermaEliminaModal({ richiesta, onCancel, onConfirm, pending }) {
  const advanced = ["in_preparazione", "pronta", "consegnata"].includes(richiesta.stato);
  return (
    <div
      className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
      onClick={onCancel}
      data-testid="modal-conferma-elimina-rmag"
    >
      <div
        className="bg-white w-full max-w-md rounded-md shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-slate-200 flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center shrink-0">
            <Trash2 className="w-5 h-5 text-rose-700" />
          </div>
          <div className="flex-1">
            <h3 className="font-display font-bold text-lg text-slate-900">
              Vuoi eliminare questa richiesta materiali?
            </h3>
            <div className="mt-2 text-xs text-slate-600 space-y-0.5">
              <div><b>Cantiere:</b> {richiesta.cantiere_nome || "—"}</div>
              <div><b>Materiale:</b> {richiesta.materiale_nome || "—"}</div>
              <div>
                <b>Q.tà:</b> {richiesta.quantita} {richiesta.unita_misura} · <b>Stato:</b>{" "}
                {STATO_META[richiesta.stato]?.label || richiesta.stato}
              </div>
            </div>
          </div>
        </div>
        {advanced && (
          <div className="mx-5 mt-4 p-3 bg-amber-50 border border-amber-200 rounded-sm flex items-start gap-2 text-xs text-amber-900">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              La richiesta è già <b>{STATO_META[richiesta.stato]?.label}</b>. Verrà spostata nel
              <b> cestino</b> (soft-delete): eventuali movimenti di magazzino già registrati NON
              vengono toccati. L'operazione è reversibile da un amministratore.
            </div>
          </div>
        )}
        <div className="p-5 flex justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={pending}
            data-testid="btn-annulla-elimina-rmag"
            className="px-4 py-2 text-sm font-semibold border border-slate-300 hover:bg-slate-50 rounded-sm"
          >
            Annulla
          </button>
          <button
            onClick={onConfirm}
            disabled={pending}
            data-testid="btn-conferma-elimina-rmag"
            className="px-4 py-2 text-sm font-bold text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-60 rounded-sm inline-flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4" />
            {pending ? "Elimino…" : "Elimina"}
          </button>
        </div>
      </div>
    </div>
  );
}

function PrelievoScannerModal({ richiesta, onClose, onUpdated }) {
  const [barcode, setBarcode] = useState("");
  const [saving, setSaving] = useState(false);
  const [live, setLive] = useState(richiesta);
  const [ultimo, setUltimo] = useState(null);

  const inputRef = useRef(null);
  const scanTimerRef = useRef(null);

  useEffect(() => {
    setLive(richiesta);
  }, [richiesta]);

  useEffect(() => {
    const timer = setTimeout(() => inputRef.current?.focus(), 100);
    return () => clearTimeout(timer);
  }, []);

  const richiesti = Number(live?.quantita || 0);
  const prelevati = Number(live?.quantita_prelevata || 0);
  const residui =
    live?.quantita_residua === null ||
    live?.quantita_residua === undefined
      ? Math.max(0, richiesti - prelevati)
      : Number(live.quantita_residua || 0);

  const completata =
    (live?.stato === "pronta" || live?.stato === "consegnata") || residui <= 0.000001;

  const scan = async (raw) => {
    const code = String(raw || "").trim();

    if (!code || saving || completata) return;

    setBarcode("");
    setSaving(true);

    try {
      const { data } = await api.post(
        `/richieste-magazzino/${live.richiesta_id}/preleva-barcode`,
        {
          barcode: code,
          quantita: 1,
        }
      );

      const aggiornata = data.richiesta;

      setLive(aggiornata);

      setUltimo({
        nome: data.materiale?.nome || live.materiale_nome,
        barcode: code,
        giacenza: data.materiale?.giacenza_residua,
      });

      onUpdated?.(aggiornata);

      if (aggiornata.stato === "pronta") {
        toast.success(
          `${aggiornata.materiale_nome} · preparazione completata · richiesta PRONTA`
        );
      } else {
        toast.success(
          `${aggiornata.materiale_nome} · -1 dal magazzino`
        );
      }
    } catch (e) {
      toast.error(
        e?.response?.data?.detail ||
        "Errore durante il prelievo del materiale"
      );
    } finally {
      setSaving(false);
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] bg-slate-950/90 backdrop-blur-sm flex items-center justify-center p-3"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="w-full max-w-2xl bg-white border border-slate-300 shadow-2xl"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="bg-slate-900 text-white p-4 flex items-center gap-3">
          <Package className="w-7 h-7 text-amber-400" />

          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest text-amber-300">
              Prelievo da magazzino
            </div>

            <div className="font-display font-black text-xl">
              {live.materiale_nome}
            </div>

            <div className="text-xs text-slate-300 mt-1">
              {live.cantiere_nome}
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-slate-800 rounded-sm"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <div className="grid grid-cols-3 gap-2">
            <div className="border border-slate-200 bg-slate-50 p-3 text-center">
              <div className="text-[10px] uppercase text-slate-500">
                Richiesti
              </div>
              <div className="text-xl font-black">
                {richiesti}
              </div>
            </div>

            <div className="border border-emerald-200 bg-emerald-50 p-3 text-center">
              <div className="text-[10px] uppercase text-emerald-700">
                Prelevati
              </div>
              <div className="text-xl font-black text-emerald-800">
                {prelevati}
              </div>
            </div>

            <div className="border border-amber-200 bg-amber-50 p-3 text-center">
              <div className="text-[10px] uppercase text-amber-700">
                Residui
              </div>
              <div className="text-xl font-black text-amber-800">
                {residui}
              </div>
            </div>
          </div>

          {completata ? (
            <div className="border-2 border-emerald-400 bg-emerald-50 p-5 text-center">
              <div className="font-black text-emerald-800 text-lg">
                ✓ PRELIEVO COMPLETATO
              </div>

              <div className="text-sm text-emerald-700 mt-1">
                La richiesta è stata impostata come Pronta.
              </div>
            </div>
          ) : (
            <>
              <div className="text-center">
                <div className="font-bold text-slate-900">
                  Scansiona il barcode del materiale richiesto
                </div>

                <div className="text-xs text-slate-500 mt-1">
                  Ogni scansione scarica immediatamente 1 {live.unita_misura || "pz"} dalla giacenza.
                </div>
              </div>

              <input
                ref={inputRef}
                value={barcode}
                onChange={(e) => {
                  const value = e.target.value;
                  setBarcode(value);

                  // Il lettore barcode scrive tutto il codice molto velocemente.
                  // Dopo 150 ms senza nuovi caratteri consideriamo terminata
                  // la scansione e preleviamo automaticamente 1 pezzo.
                  if (scanTimerRef.current) {
                    clearTimeout(scanTimerRef.current);
                  }

                  scanTimerRef.current = setTimeout(() => {
                    const code = String(value || "").trim();

                    if (code) {
                      scan(code);
                    }
                  }, 150);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();

                    if (scanTimerRef.current) {
                      clearTimeout(scanTimerRef.current);
                    }

                    scan(e.currentTarget.value);
                  }
                }}
                disabled={saving}
                autoFocus
                autoComplete="off"
                autoCapitalize="off"
                data-no-uppercase
                placeholder="Scansiona barcode oppure digitalo qui..."
                className="w-full border-2 border-amber-400 px-4 py-4 text-center font-mono text-xl font-bold outline-none focus:ring-2 focus:ring-amber-300 disabled:opacity-50"
              />

              <button
                type="button"
                disabled={saving || !barcode.trim()}
                onClick={() => scan(barcode)}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white font-black py-3 disabled:opacity-50"
              >
                {saving ? "PRELIEVO..." : "PRELEVA 1"}
              </button>
            </>
          )}

          {ultimo && (
            <div className="border border-slate-200 bg-slate-50 p-3 text-sm">
              <div className="font-bold text-emerald-700">
                Ultima scansione OK
              </div>

              <div>{ultimo.nome}</div>

              <div className="font-mono text-xs text-slate-500">
                {ultimo.barcode}
                {ultimo.giacenza !== undefined &&
                  ` · Giacenza residua ${ultimo.giacenza}`}
              </div>
            </div>
          )}

          <div className="text-[11px] text-slate-500">
            Materiale richiesto: <b>{live.materiale_nome}</b>
            <br />
            Stato richiesta: <b>{STATO_META[live.stato]?.label || live.stato}</b>
          </div>
        </div>
      </div>
    </div>
  );
}

